import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type HeroSlide = {
  id: string;
  imageUrl: string;
  headline: string;
  subheadline?: string | null;
  buttonLabel?: string | null;
  buttonLink?: string | null;
  sortOrder: number;
  isActive: boolean;
};

function toFullImageUrl(raw: string) {
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/uploads/')) return `${API_BASE}${raw}`;
  return raw;
}

function HeroPage() {
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const { t } = useTranslation();
  const location = useLocation();
  const [slides, setSlides] = React.useState<HeroSlide[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState('');
  const [headline, setHeadline] = React.useState('');
  const [subheadline, setSubheadline] = React.useState('');
  const [buttonLabel, setButtonLabel] = React.useState('Shop now');
  const [buttonLink, setButtonLink] = React.useState('#products');
  const [sortOrder, setSortOrder] = React.useState('0');
  const [isActive, setIsActive] = React.useState(true);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [translationLocale, setTranslationLocale] = React.useState('fr');
  const [translationForm, setTranslationForm] = React.useState({
    headline: '',
    subheadline: '',
    buttonLabel: '',
  });
  const [translationLoading, setTranslationLoading] = React.useState(false);
  const [translationSaving, setTranslationSaving] = React.useState(false);
  const [translationSuccess, setTranslationSuccess] = React.useState<string | null>(null);

  const loadSlides = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/content/hero-slides/workspace`);
      if (!res.ok) {
        throw new Error('Failed to load hero slides');
      }
      const data = await res.json();
      setSlides(data);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Unable to load hero slides',
      );
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch]);

  React.useEffect(() => {
    void loadSlides();
  }, [loadSlides]);

  const resetForm = () => {
    setImageFile(null);
    setImageUrl('');
    setHeadline('');
    setSubheadline('');
    setButtonLabel(t('backoffice.hero_shop_now_default'));
    setButtonLink('#products');
    setSortOrder('0');
    setIsActive(true);
    setEditingId(null);
    setTranslationSuccess(null);
    setTranslationForm({ headline: '', subheadline: '', buttonLabel: '' });
  };

  React.useEffect(() => {
    let cancelled = false;
    if (!editingId) return () => { cancelled = true; };
    void (async () => {
      setTranslationLoading(true);
      try {
        const url = new URL(`${API_BASE}/content/hero-slides/${editingId}/translations`);
        url.searchParams.set('locale', translationLocale);
        const res = await globalAuthorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          translations: {
            headline?: string;
            subheadline?: string;
            buttonLabel?: string;
          };
        };
        if (!cancelled) {
          setTranslationForm({
            headline: data.translations.headline || '',
            subheadline: data.translations.subheadline || '',
            buttonLabel: data.translations.buttonLabel || '',
          });
        }
      } finally {
        if (!cancelled) setTranslationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, globalAuthorizedFetch, translationLocale]);

  const saveTranslations = async () => {
    if (!editingId) return;
    setTranslationSaving(true);
    setTranslationSuccess(null);
    try {
      const res = await globalAuthorizedFetch(
        `${API_BASE}/content/hero-slides/${editingId}/translations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locale: translationLocale, ...translationForm }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { message?: string }).message || 'Unable to save hero translations';
        throw new Error(msg);
      }
      setTranslationSuccess('Translation saved successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save hero translations');
    } finally {
      setTranslationSaving(false);
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!headline.trim()) {
      setError(t('backoffice.hero_error_headline'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let finalImageUrl = imageUrl;
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await globalAuthorizedFetch(`${API_BASE}/content/hero-slides/image`, {
          method: 'POST',
          body: formData,
        });
        if (!uploadRes.ok) {
          throw new Error('Failed to upload image');
        }
        const uploadJson = await uploadRes.json();
        finalImageUrl = uploadJson.url;
      }
      if (!finalImageUrl) {
        setError(t('backoffice.hero_error_upload'));
        setSaving(false);
        return;
      }

      const payload = {
        imageUrl: finalImageUrl,
        headline,
        subheadline: subheadline || undefined,
        buttonLabel: buttonLabel || undefined,
        buttonLink: buttonLink || undefined,
        sortOrder: Number(sortOrder) || 0,
        isActive,
      };

      const url = editingId
        ? `${API_BASE}/content/hero-slides/${editingId}`
        : `${API_BASE}/content/hero-slides`;
      const method = editingId ? 'PATCH' : 'POST';

      const res = await globalAuthorizedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(editingId ? 'Failed to update hero slide' : 'Failed to create hero slide');
      }
      resetForm();
      await loadSlides();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Unable to create hero slide',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (slide: HeroSlide) => {
    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/content/hero-slides/${slide.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !slide.isActive }),
      });
      if (!res.ok) {
        throw new Error('Failed to update slide');
      }
      await loadSlides();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to update slide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slide: HeroSlide) => {
    const ok = window.confirm(t('backoffice.hero_confirm_delete'));
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/content/hero-slides/${slide.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error('Failed to delete slide');
      }
      await loadSlides();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to delete slide');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <div className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-slate-900">
              {t('backoffice.hero_title')}
            </h1>
            <p className="mt-1 text-xs text-slate-600">
              {t('backoffice.hero_subtitle')}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <form
            onSubmit={handleCreate}
            className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {editingId ? t('backoffice.hero_form_edit') : t('backoffice.hero_form_new')}
            </h2>
            <div>
              <label
                htmlFor="hero-image"
                className="block text-xs font-medium text-slate-700"
              >
                {t('backoffice.hero_label_image')}
              </label>
              <input
                id="hero-image"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  setImageFile(file ?? null);
                }}
                className="mt-1 block w-full text-xs text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-50 hover:file:bg-slate-700"
              />
            </div>
            <div>
              <label
                htmlFor="headline"
                className="block text-xs font-medium text-slate-700"
              >
                {t('backoffice.hero_label_headline')}
              </label>
              <input
                id="headline"
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            <div>
              <label
                htmlFor="subheadline"
                className="block text-xs font-medium text-slate-700"
              >
                {t('backoffice.hero_label_subheadline')}
              </label>
              <textarea
                id="subheadline"
                value={subheadline}
                onChange={(e) => setSubheadline(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="buttonLabel"
                  className="block text-xs font-medium text-slate-700"
                >
                  {t('backoffice.hero_label_button_label')}
                </label>
                <input
                  id="buttonLabel"
                  type="text"
                  value={buttonLabel}
                  onChange={(e) => setButtonLabel(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>
              <div>
                <label
                  htmlFor="buttonLink"
                  className="block text-xs font-medium text-slate-700"
                >
                  {t('backoffice.hero_label_button_link')}
                </label>
                <input
                  id="buttonLink"
                  type="text"
                  value={buttonLink}
                  onChange={(e) => setButtonLink(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <div>
                <label
                  htmlFor="sortOrder"
                  className="block text-xs font-medium text-slate-700"
                >
                  {t('backoffice.hero_label_sort_order')}
                </label>
                <input
                  id="sortOrder"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>
              <label className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                {t('backoffice.hero_label_active')}
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={resetForm}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                {t('backoffice.hero_btn_clear')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center rounded-full bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
              >
                {saving ? t('common.saving') : editingId ? t('backoffice.hero_btn_save') : t('backoffice.hero_btn_add_slide')}
              </button>
            </div>
            {editingId ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Dynamic content translations
                  </p>
                  <select
                    value={translationLocale}
                    onChange={(e) => {
                      setTranslationLocale(e.target.value);
                      setTranslationSuccess(null);
                    }}
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                  >
                    <option value="fr">French (fr)</option>
                    <option value="es">Spanish (es)</option>
                    <option value="de">German (de)</option>
                  </select>
                </div>
                {translationLoading ? (
                  <p className="text-xs text-slate-500">Loading translations...</p>
                ) : (
                  <div className="grid gap-2">
                    <input
                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
                      placeholder="Localized headline"
                      value={translationForm.headline}
                      onChange={(e) => {
                        setTranslationForm((p) => ({ ...p, headline: e.target.value }));
                        setTranslationSuccess(null);
                      }}
                    />
                    <textarea
                      rows={2}
                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
                      placeholder="Localized subheadline"
                      value={translationForm.subheadline}
                      onChange={(e) => {
                        setTranslationForm((p) => ({ ...p, subheadline: e.target.value }));
                        setTranslationSuccess(null);
                      }}
                    />
                    <input
                      className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs"
                      placeholder="Localized button label"
                      value={translationForm.buttonLabel}
                      onChange={(e) => {
                        setTranslationForm((p) => ({ ...p, buttonLabel: e.target.value }));
                        setTranslationSuccess(null);
                      }}
                    />
                    <div>
                      <button
                        type="button"
                        onClick={saveTranslations}
                        disabled={translationSaving}
                        className="rounded border border-brand-500 bg-white px-3 py-1.5 text-xs font-semibold text-brand-700 disabled:opacity-60"
                      >
                        {translationSaving ? 'Saving translation...' : 'Save translation'}
                      </button>
                      {translationSuccess ? (
                        <p className="mt-2 text-xs font-medium text-emerald-700">
                          {translationSuccess}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </form>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t('backoffice.hero_existing_slides')}
              </h2>
              {loading && (
                <span className="text-[11px] text-slate-500">
                  {t('backoffice.hero_loading')}
                </span>
              )}
            </div>
            <div className="space-y-3">
              {slides.length === 0 && !loading && (
                <p className="text-xs text-slate-500">
                  {t('backoffice.hero_empty')}
                </p>
              )}
              {slides.map((slide) => (
                <div
                  key={slide.id}
                  className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-sm"
                >
                  <div className="h-16 w-28 flex-shrink-0 overflow-hidden rounded-md bg-slate-100">
                    <img
                      src={toFullImageUrl(slide.imageUrl)}
                      alt={slide.headline}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-slate-900">
                          {slide.headline}
                        </p>
                        {slide.subheadline && (
                          <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-600">
                            {slide.subheadline}
                          </p>
                        )}
                      </div>
                      <span className="whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                        {t('backoffice.hero_order')} {slide.sortOrder}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <div className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span>
                          {t('backoffice.hero_button')}:{' '}
                          {slide.buttonLabel || t('backoffice.hero_shop_now_default')}
                        </span>
                        <span className="hidden sm:inline">
                          {t('backoffice.hero_link')}:{' '}
                          {slide.buttonLink || '#products'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(slide.id);
                            setImageFile(null);
                            setImageUrl(slide.imageUrl);
                            setHeadline(slide.headline);
                            setSubheadline(slide.subheadline ?? '');
                            setButtonLabel(slide.buttonLabel ?? 'Shop now');
                            setButtonLink(slide.buttonLink ?? '#products');
                            setSortOrder(String(slide.sortOrder));
                            setIsActive(slide.isActive);
                          }}
                          className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-800 hover:bg-slate-200"
                        >
                          {t('backoffice.hero_btn_edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(slide)}
                          className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                            slide.isActive
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {slide.isActive ? t('backoffice.hero_active') : t('backoffice.hero_inactive')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(slide)}
                          className="rounded-full bg-red-50 px-3 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-100"
                        >
                          {t('backoffice.hero_btn_delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default HeroPage;

